import { inject, Injectable } from '@angular/core';
import {
  AppConfig,
  BookingWizardCoverageDefault,
  DropoffCoverTier,
  PickupReviewFlow,
  RecommendedBadgeVariant,
  SharedAppSettings,
  SharedAppSettingsService,
} from '@luggagehero/shared/app-settings/data-access';
import { BookableStorageLocation, StorageLocationService } from '@luggagehero/shared/interfaces';
import { SharedSplitTestingService } from '@luggagehero/shared/services/split-testing';
import { SharedUtilDate } from '@luggagehero/shared/util';
import { combineLatest, distinctUntilChanged, map, Observable, shareReplay } from 'rxjs';

import { resolveFeature } from './resolve-feature';

/** The api's own fallback for `FREE_CANCELLATION_LOCAL_CUTOFF_HOUR`; see `freeCancellationCutoffTime`. */
const DEFAULT_FREE_CANCELLATION_HOUR = 9;

/**
 * A remote setting counts as a boolean only if it IS one. `AppConfig.override` type-checks a remote
 * value only against a key AppConfig already declares; for any other key it assigns whatever the
 * `appsettings` row holds. So a row written as the string `"false"` arrives as a truthy string, and
 * a switch reading it raw would do the opposite of what the row says — show an action that was
 * meant to be withdrawn. Anything that is not a real boolean is treated as absent, so the default
 * applies and a mistyped row fails safe instead of inverted.
 */
const strictBoolean = (value: unknown): boolean | undefined => (typeof value === 'boolean' ? value : undefined);

/**
 * AM-4924 — the drop-off cover tiers that exist, keyed so a stored or URL value is checked by own
 * property. A `Record` over the union, so adding a tier to `DropoffCoverTier` without adding it here
 * fails to compile rather than silently never being offered.
 */
const DROPOFF_COVER_TIER_NAMES: Readonly<Record<DropoffCoverTier, true>> = { basic: true, premium: true };

/** What drop-off offers with no `dropoffCoverTiers` row: the booking's own tiers (offer model a). */
const DROPOFF_COVER_TIERS_FALLBACK: readonly DropoffCoverTier[] = ['basic', 'premium'];

/**
 * Resolves UI/UX decisions that depend on durable configuration (app settings) and/or
 * transient experiment assignment (split-testing variants).
 *
 * This is the layer that was missing in the codebase: settings and variants were each read
 * directly, and feature interpretation leaked into `SharedSplitTestingService` (which should
 * be pure variant plumbing) and got duplicated across components. Each decision lives here as
 * one resolver, built on the shared {@link resolveFeature} precedence primitive (flag wins →
 * variant → fallback).
 *
 * Each decision is exposed two ways:
 *   - a **sync getter** (`pickupReviewFlow`) for legacy render-time reads (template getters,
 *     called post-init);
 *   - an **observable** (`pickupReviewFlow$`) for reactive consumers. The stream emits only
 *     once both sources are ready (settings loaded + split-testing initialized), so
 *     `firstValueFrom(resolver.x$)` is a drop-in replacement for the old
 *     `await splitTesting.isVariantEnabled(...)` and waits for readiness the same way.
 *
 * New work should prefer the observables (ngrx direction); the sync getters are the bridge
 * for existing promise/getter-based legacy code.
 */
@Injectable({ providedIn: 'root' })
export class FeatureResolver {
  private readonly appSettings = inject(SharedAppSettingsService);
  private readonly splitTesting = inject(SharedSplitTestingService);

  // --- Pickup review flow (AM-4364) ------------------------------------------------------
  /** Which pickup-review flow is active for this client. */
  public get pickupReviewFlow(): PickupReviewFlow {
    return this.resolvePickupReviewFlow(this.appSettings.current, this.splitTesting.variant);
  }
  public readonly pickupReviewFlow$: Observable<PickupReviewFlow> = this.stream((s, v) =>
    this.resolvePickupReviewFlow(s, v),
  );

  // --- Daily pricing as the default pricing model ---------------------------------------
  /** Whether daily pricing should be the default pricing model. */
  public get isDailyPricingDefault(): boolean {
    return this.resolveDailyPricingDefault(this.appSettings.current, this.splitTesting.variant);
  }
  public readonly isDailyPricingDefault$: Observable<boolean> = this.stream((s, v) =>
    this.resolveDailyPricingDefault(s, v),
  );

  // --- New guest dropoff flow (needs storage-location context) --------------------------
  /** Whether the new guest dropoff flow should be used for the given storage location. */
  public isNewGuestDropoffFlow(storageLocation: BookableStorageLocation | undefined): boolean {
    return this.resolveNewGuestDropoffFlow(this.appSettings.current, this.splitTesting.variant, storageLocation);
  }
  public isNewGuestDropoffFlow$(storageLocation: BookableStorageLocation | undefined): Observable<boolean> {
    return this.stream((s, v) => this.resolveNewGuestDropoffFlow(s, v, storageLocation));
  }

  /**
   * Whether the guest flow may ask "Are you a guest?" and hand a "no" to the walk-in wizard at this
   * shop (AM-4704) — the switch `hotelsHeroMarketplaceHybrid`. Settings only: no variant, no
   * fallback to on. Off means the question is never shown and the shop's flow is what it was before.
   */
  public isHotelsHeroMarketplaceHybrid(storageLocation: BookableStorageLocation | undefined): boolean {
    return this.resolveHotelsHeroMarketplaceHybrid(this.appSettings.current, storageLocation);
  }

  // --- Booking wizard (AM-4334) ----------------------------------------------------------
  /** Whether the new booking wizard replaces the legacy booking surface. */
  public get isBookingWizard(): boolean {
    return this.resolveBookingWizard(this.appSettings.current, this.splitTesting.variant);
  }
  public isBookingWizard$(): Observable<boolean> {
    return this.stream((s, v) => this.resolveBookingWizard(s, v));
  }
  private resolveBookingWizard(settings: SharedAppSettings | undefined, variant: string | null): boolean {
    return resolveFeature({
      flag: settings?.bookingWizard,
      variant,
      fromVariant: (v) => (v === AppConfig.EXPERIMENT_VARIANTS.bookingWizard ? true : undefined),
      fallback: false,
    });
  }

  // --- Walk-in drop-off wizard (AM-4612) -------------------------------------------------
  /**
   * Whether the walk-in drop-off wizard replaces the legacy drop-off step flow at `book-shop/:id`.
   *
   * Split-tested against that legacy flow as the control, the same shape as `bookingWizard`: the
   * experiment variant turns the wizard on for the assigned arm, and the `walkInDropoffWizard`
   * settings flag overrides it either way — `true` promotes the wizard to everyone (how a winning
   * experiment is rolled out), `false` is the kill switch.
   *
   * **Never true for a guest (hotel) location**, whatever the variant or flag says — the guest
   * drop-off flow owns those, and the exclusion is enforced inside this resolver rather than left to
   * call-order in the component. A traveler assigned to the walk-in arm therefore still gets the
   * guest flow at a hotel, which is correct: the two flows can't both claim a shop.
   */
  public isWalkInDropoffWizard(storageLocation: BookableStorageLocation | undefined): boolean {
    return this.resolveWalkInDropoffWizard(this.appSettings.current, this.splitTesting.variant, storageLocation);
  }
  public isWalkInDropoffWizard$(storageLocation: BookableStorageLocation | undefined): Observable<boolean> {
    return this.stream((s, v) => this.resolveWalkInDropoffWizard(s, v, storageLocation));
  }
  private resolveWalkInDropoffWizard(
    settings: SharedAppSettings | undefined,
    variant: string | null,
    storageLocation: BookableStorageLocation | undefined,
  ): boolean {
    // Stand down for any shop that offers guest storage — keyed on the **service**, not on whether
    // the new guest flow happens to win. Those differ: `resolveNewGuestDropoffFlow` returns false
    // for a guest shop sitting on the `storageLocationsUsingOldGuestDropoffFlow` denylist, so gating
    // on it would let the walk-in wizard render at a hotel that was merely pinned to the *old* guest
    // flow — no verification, no PIN provisioning, and the booking tagged `walk_in` instead of
    // `guest`. A guest shop belongs to one of the guest flows, old or new, on entry. The one way into
    // this wizard at a guest shop is the traveler's own "not a guest" answer in the new guest flow
    // (AM-4704), which the drop-off page acts on directly; this resolver is not consulted for it.
    if (storageLocation?.services?.includes(StorageLocationService.GuestLuggageStorage)) {
      return false;
    }
    return resolveFeature<boolean>({
      flag: settings?.walkInDropoffWizard,
      variant,
      fromVariant: (v) => (v === AppConfig.EXPERIMENT_VARIANTS.walkInDropoffWizard ? true : undefined),
      fallback: false,
    });
  }

  // --- Find-shop storage-time prompt (AM-4799 / AM-4798) ---------------------------------
  /**
   * Whether find-shop asks "When do you need to store your bags?" before showing results.
   *
   * Split-tested 50/50 against no prompt as the control, the same shape as `bookingWizard`: the
   * experiment variant turns the prompt on for the assigned arm, and the `findShopTimePrompt`
   * settings flag overrides it either way — `true` promotes the prompt to everyone (how a winning
   * experiment is rolled out), `false` is the kill switch. The prompt (AM-4801) must read this and
   * nothing else — never the split-testing service directly — so the flag's precedence holds.
   */
  public get isFindShopTimePrompt(): boolean {
    return this.resolveFindShopTimePrompt(this.appSettings.current, this.splitTesting.variant);
  }
  public readonly isFindShopTimePrompt$: Observable<boolean> = this.stream((s, v) =>
    this.resolveFindShopTimePrompt(s, v),
  );
  private resolveFindShopTimePrompt(settings: SharedAppSettings | undefined, variant: string | null): boolean {
    return resolveFeature<boolean>({
      flag: settings?.findShopTimePrompt,
      variant,
      fromVariant: (v) => (v === AppConfig.EXPERIMENT_VARIANTS.findShopTimePrompt ? true : undefined),
      fallback: false,
    });
  }

  // --- Guest drop-off coverage cards (AM-4899, needs storage-location context) ------------
  /**
   * Whether the guest drop-off coverage step shows the new-design cards instead of today's, for the given
   * storage location: true at the hotels in `GUEST_DROPOFF_COVERAGE_CARDS_LOCATIONS`, false everywhere
   * else. The split test ended in favour of the cards (14 Oct 2026), so no variant is read any more.
   */
  public isGuestDropoffCoverageCards(storageLocation: BookableStorageLocation | undefined): boolean {
    return this.resolveGuestDropoffCoverageCards(storageLocation);
  }
  public isGuestDropoffCoverageCards$(storageLocation: BookableStorageLocation | undefined): Observable<boolean> {
    return this.stream(() => this.resolveGuestDropoffCoverageCards(storageLocation));
  }
  private resolveGuestDropoffCoverageCards(storageLocation: BookableStorageLocation | undefined): boolean {
    return !!storageLocation?._id && AppConfig.GUEST_DROPOFF_COVERAGE_CARDS_LOCATIONS.includes(storageLocation._id);
  }

  // --- Premium insurance tier (AM-4345) --------------------------------------------------
  /**
   * Whether the **booking wizard** offers the Premium coverage tier alongside Basic (AM-4345). A rollout
   * switch, no experiment mapping.
   *
   * **Both traveler apps ship it `true`**, so the tier is live from the deploy — the split test runs from
   * day one rather than waiting on a flag flip. The resolver's own fallback is still `false`, which covers
   * the moment before settings have loaded (a client should not flash a tier it may not be allowed to sell).
   *
   * Because it ships on, the **pricing** must be right *before* deploying, not before some later switch:
   * AM-4619 tracks four contexts (WW B2C, SE, NO, AR) that price Premium below Basic. Those are refused —
   * the card is hidden client-side and the server declines the tier — so nothing is mis-sold, but the
   * experiment does not run there. Withdrawing the tier now means setting `false` in the `appsettings`
   * collection, per `appName`.
   *
   * Deliberately **not** the hotel-guest flow's `premiumInsuranceEnabled`, which is a different, already
   * live switch.
   *
   * Offering the tier still requires the shop's pricing context to actually price it — that is the quote's
   * business, not this flag's.
   */
  public get isPremiumInsuranceEnabled(): boolean {
    return this.resolvePremiumInsuranceEnabled(this.appSettings.current);
  }
  public readonly isPremiumInsuranceEnabled$: Observable<boolean> = this.stream((s) =>
    this.resolvePremiumInsuranceEnabled(s),
  );

  // --- External review prompt minimum rating (AM-4364) -----------------------------------
  /**
   * Minimum (initial) star rating required before the external (e.g. Google) review prompt
   * is shown after publishing. Tunable via the `externalReviewMinRating` app setting (e.g.
   * tighten to 5) without re-releasing the apps; falls back to 4.
   */
  public get externalReviewMinRating(): number {
    return this.resolveExternalReviewMinRating(this.appSettings.current, this.splitTesting.variant);
  }
  public readonly externalReviewMinRating$: Observable<number> = this.stream((s, v) =>
    this.resolveExternalReviewMinRating(s, v),
  );

  // --- Native store review minimum rating (AM-4364) --------------------------------------
  /**
   * Minimum (initial) star rating required before the native App Store / Play Store in-app
   * review is requested (ionic app only). Tunable via the `storeReviewMinRating` app setting;
   * falls back to 4. Independent from {@link externalReviewMinRating} so the two prompts can
   * be tuned separately.
   */
  public get storeReviewMinRating(): number {
    return this.resolveStoreReviewMinRating(this.appSettings.current, this.splitTesting.variant);
  }
  public readonly storeReviewMinRating$: Observable<number> = this.stream((s, v) =>
    this.resolveStoreReviewMinRating(s, v),
  );

  // --- Trustpilot review fallback URL (AM-4479) ------------------------------------------
  /**
   * The LuggageHero Trustpilot page the external review prompt falls back to when a shop has
   * no effective external (Google/GBP) review URL. Tunable via the `trustpilotReviewUrl` app
   * setting, which doubles as the on/off switch: unset → the default LuggageHero page (fallback
   * enabled); `''` → disabled; any other URL → that URL. Changeable without re-releasing the apps.
   */
  public get trustpilotReviewUrl(): string {
    return this.resolveTrustpilotReviewUrl(this.appSettings.current, this.splitTesting.variant);
  }
  public readonly trustpilotReviewUrl$: Observable<string> = this.stream((s, v) =>
    this.resolveTrustpilotReviewUrl(s, v),
  );

  // --- Service fee footnote (AM-4449; needs storage-location context for city scoping) ---
  /**
   * Whether the "+ {fee} service fee" footnote should be shown for the given shop. Driven
   * purely by app settings (no experiment): the global `isServiceFeeFootnoteEnabled` switch
   * ORed with a per-city allowlist (`serviceFeeFootnoteCities`, matched on the shop's
   * `officialLocationKey`). Defaults to hidden when neither is set.
   */
  public showServiceFeeFootnote(storageLocation: BookableStorageLocation | undefined): boolean {
    return this.resolveServiceFeeFootnote(this.appSettings.current, storageLocation);
  }
  public showServiceFeeFootnote$(storageLocation: BookableStorageLocation | undefined): Observable<boolean> {
    return this.stream((s) => this.resolveServiceFeeFootnote(s, storageLocation));
  }

  // --- Pending-activation education block (AM-4486) --------------------------------------
  /**
   * Whether the pending-activation education block replaces the home screen's stat panels
   * (+ partner-program card + list stat columns) for a never-activated location. No experiment
   * variant — purely a rollout kill switch, defaults to enabled.
   */
  public get isPendingActivationEducationEnabled(): boolean {
    return this.resolvePendingActivationEducationEnabled(this.appSettings.current);
  }
  public readonly isPendingActivationEducationEnabled$: Observable<boolean> = this.stream((s) =>
    this.resolvePendingActivationEducationEnabled(s),
  );

  // --- Onboarding-guide banner on the partner home screen -------------------------------
  /**
   * Whether the "how it works" banner — which links out to the onboarding PDF while a storage
   * location has never been activated — shows on the partner home screen. No experiment variant;
   * a rollout kill switch, defaults to enabled.
   */
  public get isOnboardingGuideBannerEnabled(): boolean {
    return this.resolveOnboardingGuideBannerEnabled(this.appSettings.current);
  }
  public readonly isOnboardingGuideBannerEnabled$: Observable<boolean> = this.stream((s) =>
    this.resolveOnboardingGuideBannerEnabled(s),
  );

  // --- Guest drop-off: the first step's visible footer (AM-4963) --------------------------
  /**
   * Whether the guest drop-off's first step shows its disabled Get started with a hint before the
   * question is answered, instead of hiding the footer until then. No experiment variant; a rollout
   * kill switch, defaults to enabled.
   */
  public get isGuestDropoffFirstStepHintEnabled(): boolean {
    return this.resolveGuestDropoffFirstStepHint(this.appSettings.current);
  }
  public readonly isGuestDropoffFirstStepHintEnabled$: Observable<boolean> = this.stream((s) =>
    this.resolveGuestDropoffFirstStepHint(s),
  );

  // --- Booking wizard: accumulated storage rate on the pricing-model card (AM-4637/AM-4661) ---
  /**
   * Whether the **booking wizard's** select-storage card shows the accumulated price for every bag
   * in the booking (`$9/day` for 2 bags) instead of the per-bag rate (`$4.50/bag/day`). Defaults to
   * the per-bag rate — today's presentation.
   *
   * Scoped to the wizard flows by name and by wiring: the guest drop-off step renders the same card
   * and must not read this (AM-4661). See the setting's own doc comment for why.
   *
   * A settings flag rather than a release, so the two presentations can be compared and the winner
   * chosen without shipping an app update. No experiment variant maps to it yet; if this becomes an
   * A/B test, add the mapping in {@link resolveBookingWizardAccumulatedStorageRate} — the flag will still win,
   * which is how a winning variant gets promoted to everyone.
   */
  public get bookingWizardAccumulatedStorageRate(): boolean {
    return this.resolveBookingWizardAccumulatedStorageRate(this.appSettings.current, this.splitTesting.variant);
  }
  public readonly bookingWizardAccumulatedStorageRate$: Observable<boolean> = this.stream((s, v) =>
    this.resolveBookingWizardAccumulatedStorageRate(s, v),
  );

  // --- Booking wizard: coverage skip link (AM-4653) --------------------------------------
  /**
   * Whether the wizard's coverage step declines via a discrete link *below* the option group
   * (`true`) instead of a third "No coverage" card *inside* it (`false`, how AM-4345 shipped).
   *
   * Both presentations reach the same state — an explicit decline — so this is purely how the
   * choice is framed: a card gives declining equal visual weight to the priced tiers, a link makes
   * it the quieter way out. Both traveler apps ship `true`; the fallback stays `false` so the moment
   * before settings load renders what shipped rather than flashing the new arrangement.
   */
  public get showCoverageSkipLink(): boolean {
    return this.resolveCoverageSkipLink(this.appSettings.current);
  }
  public readonly showCoverageSkipLink$: Observable<boolean> = this.stream((s) => this.resolveCoverageSkipLink(s));

  // --- Booking wizard: the coverage tier the insurance step opens with (AM-4919) ---------------
  /**
   * Which tier the wizard's insurance step preselects: `'none'` (nothing highlighted, the traveler
   * must pick — how the wizard shipped) or `'basic'` (Basic highlighted and Next enabled, as the
   * legacy flow's checkbox). Pre-booking flow only — the step ignores it on walk-in. A stored row
   * holding anything but the two values counts as absent, so a mistyped row falls back to `'none'`
   * rather than preselecting by accident — the same fail-safe as `strictBoolean`. Fallback `'none'`
   * so the moment before settings load renders what shipped.
   *
   * The booking wizard's switch only: the drop-off wizard's cover offer (AM-4924) always opens with
   * nothing chosen (AM-5060).
   */
  public get bookingWizardCoverageDefault(): BookingWizardCoverageDefault {
    return this.resolveCoverageDefault(this.appSettings.current);
  }
  public readonly bookingWizardCoverageDefault$: Observable<BookingWizardCoverageDefault> = this.stream((s) =>
    this.resolveCoverageDefault(s),
  );

  // --- Booking wizard: service-fee note on the storage cards (AM-4653) -------------------
  /**
   * Whether the wizard's select-storage cards carry a subdued "+ {fee} service fee" line under the
   * price. Shop-agnostic, unlike {@link showServiceFeeFootnote}: by this point the traveler has
   * already chosen a shop, so there is no rollout-by-city question left to answer — the only
   * question is whether the wizard states the fee at the moment of choosing.
   */
  public get showBookingWizardServiceFeeNote(): boolean {
    return this.resolveBookingWizardServiceFeeNote(this.appSettings.current);
  }
  public readonly showBookingWizardServiceFeeNote$: Observable<boolean> = this.stream((s) =>
    this.resolveBookingWizardServiceFeeNote(s),
  );

  // --- Booking wizard: free-cancellation banner (AM-4653) --------------------------------
  /**
   * Whether a neutral "Free cancellation" banner sits above the wizard's docked footer. Reassurance
   * carried through the flow rather than stated once and scrolled past.
   *
   * The flag only says *may*; **where** it shows is the wizard's call and is narrower than this flag
   * (AM-4667): pre-booking only, and not on the payment step. A walk-in traveler is handing bags over
   * now and has no forward reservation to cancel — see `freeCancellationBannerVisible`. Nor where the
   * quote prices a cancellation fee (AM-4788): "free cancellation" is then only true until a deadline.
   */
  public get showFreeCancellationBanner(): boolean {
    return this.resolveFreeCancellationBanner(this.appSettings.current);
  }
  public readonly showFreeCancellationBanner$: Observable<boolean> = this.stream((s) =>
    this.resolveFreeCancellationBanner(s),
  );

  // --- Booking wizard: confirm declining coverage (AM-4653) ------------------------------
  /**
   * Whether declining coverage raises the "No coverage selected" confirmation before the flow moves
   * on — the same second look the hotel-guest drop-off flow gives it.
   *
   * The one flag here whose fallback is **`true`**: the other three are presentation, but this one
   * guards an uninsured booking, and the safe answer before settings load is to ask.
   */
  public get confirmNoCoverage(): boolean {
    return this.resolveConfirmNoCoverage(this.appSettings.current);
  }
  public readonly confirmNoCoverage$: Observable<boolean> = this.stream((s) => this.resolveConfirmNoCoverage(s));

  // --- Booking wizard: the insurance step (AM-4925) ---------------------------------------
  /**
   * Whether the **pre-booking** wizard includes its insurance step. `false` takes the step out of the
   * flow: booking · select storage · payment, with the booking created uncovered, so the drop-off cover
   * offer is the only place cover is offered.
   *
   * Split-tested 50/50 against the step (the control), the same shape as {@link isDropoffCoverOffer}:
   * the `bookingWizardWithoutInsuranceStep` variant takes the step out for its arm, and the
   * `bookingWizardInsuranceStep` settings flag overrides it either way — `false` promotes the variant
   * to everyone, `true` restores the step for everyone. Fallback **on** (the step stays), which also
   * covers the moment before settings load. Only a real boolean counts: a stored `"false"` string is
   * not a switch-off.
   *
   * A step-level switch rather than a one-off: the first per-step `enabled` property of the wizard's
   * step list, for a backend step config (AM-4996) to absorb. The walk-in flow is not governed by it —
   * the wizard applies it to the pre-booking step list only.
   */
  public get isBookingWizardInsuranceStep(): boolean {
    return this.resolveBookingWizardInsuranceStep(this.appSettings.current, this.splitTesting.variant);
  }
  public readonly isBookingWizardInsuranceStep$: Observable<boolean> = this.stream((s, v) =>
    this.resolveBookingWizardInsuranceStep(s, v),
  );
  private resolveBookingWizardInsuranceStep(settings: SharedAppSettings | undefined, variant: string | null): boolean {
    return resolveFeature<boolean>({
      flag: strictBoolean(settings?.bookingWizardInsuranceStep),
      variant,
      fromVariant: (v) => (v === AppConfig.EXPERIMENT_VARIANTS.bookingWizardWithoutInsuranceStep ? false : undefined),
      fallback: true,
    });
  }

  // --- Drop-off wizard (AM-4924) -----------------------------------------------------------
  /**
   * Whether a confirmed pre-booking drops off through the drop-off wizard rather than the legacy
   * drop-off sheet. A plain flag with no experiment arm: the wizard is the drop-off for everyone once
   * it is on, and the flag exists so the check-in path can fall back to the sheet without a release.
   * Fallback **off**, which also covers the moment before settings load. Only `true` the boolean
   * counts.
   */
  public get isDropoffWizard(): boolean {
    return strictBoolean(this.appSettings.current?.dropoffWizard) === true;
  }
  public readonly isDropoffWizard$: Observable<boolean> = this.stream((s) => strictBoolean(s?.dropoffWizard) === true);

  /**
   * Whether the drop-off wizard's confirm step shows the order summary after the traveler changed the
   * booking in the wizard. A plain flag, fallback **off**; only `true` the boolean counts.
   */
  public get isDropoffWizardOrderSummaryOnConfirm(): boolean {
    return strictBoolean(this.appSettings.current?.dropoffWizardOrderSummaryOnConfirm) === true;
  }
  public readonly isDropoffWizardOrderSummaryOnConfirm$: Observable<boolean> = this.stream(
    (s) => strictBoolean(s?.dropoffWizardOrderSummaryOnConfirm) === true,
  );

  // --- Booking value without unselected insurance (AM-5017) -------------------------------
  /**
   * Whether the booking's estimated price leaves out insurance the traveler was offered but did not
   * take. That estimate is the value sent with `bookingCreated` and one input to the pick-up tip
   * ceiling, so both follow this switch. A plain flag, fallback **off**, which also
   * covers the moment before settings load; only `true` the boolean counts. Off keeps today's value,
   * because switching it on is a conversion-value change for Google Ads and must be timed.
   */
  public get isBookingValueExcludingUnselectedInsurance(): boolean {
    return strictBoolean(this.appSettings.current?.bookingValueExcludesUnselectedInsurance) === true;
  }
  public readonly isBookingValueExcludingUnselectedInsurance$: Observable<boolean> = this.stream(
    (s) => strictBoolean(s?.bookingValueExcludesUnselectedInsurance) === true,
  );

  // --- Drop-off cover offer (AM-4924) -----------------------------------------------------
  /**
   * Whether a pre-booking without cover is offered it as the traveler drops off (CTR-28 Variant A).
   * Same shape as {@link isFindShopTimePrompt}: the `dropoffCoverOffer` flag wins either way (`true`
   * promotes, `false` kills), the experiment variant turns it on for its arm, fallback **off** — which
   * also covers the moment before settings load, so the timeline never routes into the offer and then
   * has to back out. Only `true` the boolean counts: a stored `"true"` string is not a switch-on.
   *
   * Which bookings qualify, and whether one already has cover, is the drop-off flow's business — this
   * only answers whether the offer runs at all for this client.
   */
  public get isDropoffCoverOffer(): boolean {
    return this.resolveDropoffCoverOffer(this.appSettings.current, this.splitTesting.variant);
  }
  public readonly isDropoffCoverOffer$: Observable<boolean> = this.stream((s, v) =>
    this.resolveDropoffCoverOffer(s, v),
  );

  /**
   * Which tiers the drop-off offer shows (AM-4924). The offer model is undecided (CTR-28, 1 Oct): the
   * booking's own Basic + Premium, or Basic plus a cheaper tier that does not exist yet — so the list
   * is a setting. Fallback is the booking's own pair.
   *
   * Deny-by-default: only the tiers that exist survive (own-property lookup, so `constructor` or
   * `toString` from a crafted row or URL cannot), duplicates collapse, and anything that is not an
   * array — a string from a mistyped row, say — counts as an empty list, which means **no offer**
   * rather than the fallback. A row exists to say something, and a row that says nothing readable is
   * safer read as "offer nothing" than as "offer everything".
   */
  public get dropoffCoverTiers(): readonly DropoffCoverTier[] {
    return this.resolveDropoffCoverTiers(this.appSettings.current);
  }
  public readonly dropoffCoverTiers$: Observable<readonly DropoffCoverTier[]> = this.stream((s) =>
    this.resolveDropoffCoverTiers(s),
  ).pipe(
    // `stream` compares by reference, and this projection builds a fresh array every time.
    distinctUntilChanged((a, b) => a.length === b.length && a.every((tier, i) => tier === b[i])),
  );

  // --- Recommended-shop badge label (AM-4673) --------------------------------------------
  /**
   * Which label the "recommended shop" badge carries on the find-shop card.
   *
   * Settings-only, no experiment mapping. Falls back to `'superhero'`, which is what shipped
   * before this setting existed — including for the window before settings load, so the badge
   * never flickers from one label to another.
   *
   * **Migration.** This replaces `AppConfig.IS_SUPERHERO_TERMINOLOGY_ENABLED`, whose one stored
   * row (`web-traveler`, `true`) meant the same thing as the fallback here — so removing it
   * changes nothing on its own. A row for the old name is now inert and can be deleted; a row
   * for this setting is what moves the badge.
   */
  public get recommendedBadgeVariant(): RecommendedBadgeVariant {
    return this.resolveRecommendedBadgeVariant(this.appSettings.current);
  }
  public readonly recommendedBadgeVariant$: Observable<RecommendedBadgeVariant> = this.stream((s) =>
    this.resolveRecommendedBadgeVariant(s),
  );

  // --- Free-cancellation policy hour (AM-4788) -------------------------------------------
  /**
   * The policy hour as the copy states it — "9:00am" or "09:00", in the VIEWER's convention.
   *
   * Feeds the `{{time}}` token on the surfaces that state the rule rather than a booking's pinned
   * instant (shop card, shop-details pitch and USP, the policy tooltip). The number is the
   * server's (`freeCancellationCutoffHour` on `GET /v1/settings`), so moving the policy to 08:00
   * needs no copy change.
   *
   * NEVER returns empty: an unfilled `{{time}}` renders as literal braces to the traveler, which is
   * worse than a stale hour. Before settings load — and if the field is ever absent — it falls back
   * to 9, the same default the api's own constant carries, so the copy reads correctly either way.
   */
  public get freeCancellationCutoffTime(): string {
    return this.resolveFreeCancellationCutoffTime(this.appSettings.current);
  }

  // --- Confirm-booking "More info": rule vs pinned deadline (AM-4788) --------------------
  /**
   * Whether the confirm-booking "More info" window states this booking's PINNED free-cancellation
   * deadline instead of the policy rule, where a cancellation fee is priced.
   *
   * Defaults to **false**: the window states the rule, which is what the copy review settled on.
   * The pinned instant is the alternative — more specific, but it describes one booking rather than
   * the policy — and it is kept behind this switch so it can be compared without a release.
   *
   * No experiment variant maps to it; it is a plain server-overridable switch. `strictBoolean`
   * keeps a row written as the string `"false"` from reading as truthy and silently inverting it.
   */
  public get showFreeCancellationMoreInfoPinnedDeadline(): boolean {
    return this.resolveFreeCancellationMoreInfoPinnedDeadline(this.appSettings.current);
  }
  public readonly showFreeCancellationMoreInfoPinnedDeadline$: Observable<boolean> = this.stream((s) =>
    this.resolveFreeCancellationMoreInfoPinnedDeadline(s),
  );

  private resolveFreeCancellationMoreInfoPinnedDeadline(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: strictBoolean(settings?.freeCancellationMoreInfoPinnedDeadline),
      variant: null,
      // Plain switch — no experiment variant maps to it.
      fromVariant: () => undefined,
      fallback: false,
    });
  }

  private resolveFreeCancellationCutoffTime(settings: SharedAppSettings | undefined): string {
    const hour = settings?.freeCancellationCutoffHour;
    return SharedUtilDate.formatPolicyHour(hour) || SharedUtilDate.formatPolicyHour(DEFAULT_FREE_CANCELLATION_HOUR);
  }

  // --- PayPal as a payment option (AM-4816) ----------------------------------------------
  /**
   * Whether PayPal is offered as a payment option. The durable `IS_PAYPAL_ENABLED` app setting is
   * the switch — stored per `appName`, so the traveler web app offers it while the native apps keep
   * it off (production today; re-enabling native PayPal is AM-3747). No experiment maps to it.
   *
   * Default-closed: the fallback is `false`, which also covers the moment before settings load, so
   * a client never flashes a PayPal button it may not offer. The legacy selectors still read
   * `AppConfig.IS_PAYPAL_ENABLED` directly; new surfaces — the booking wizard first — read this and
   * nothing else, so the flag has one precedence rule wherever it is consulted.
   */
  public get isPaypalEnabled(): boolean {
    return this.resolvePaypalEnabled(this.appSettings.current);
  }
  public readonly isPaypalEnabled$: Observable<boolean> = this.stream((s) => this.resolvePaypalEnabled(s));

  // --- Pure resolvers (no side effects; unit-tested directly via the public API) ---------

  private resolveDropoffCoverOffer(settings: SharedAppSettings | undefined, variant: string | null): boolean {
    return resolveFeature<boolean>({
      flag: strictBoolean(settings?.dropoffCoverOffer),
      variant,
      fromVariant: (v) => (v === AppConfig.EXPERIMENT_VARIANTS.dropoffCoverOffer ? true : undefined),
      fallback: false,
    });
  }

  private resolveDropoffCoverTiers(settings: SharedAppSettings | undefined): readonly DropoffCoverTier[] {
    const raw: unknown = settings?.dropoffCoverTiers;
    if (raw === undefined) {
      return DROPOFF_COVER_TIERS_FALLBACK;
    }
    if (!Array.isArray(raw)) {
      return [];
    }
    const tiers: DropoffCoverTier[] = [];
    for (const entry of raw) {
      if (
        typeof entry === 'string' &&
        Object.prototype.hasOwnProperty.call(DROPOFF_COVER_TIER_NAMES, entry) &&
        !tiers.includes(entry as DropoffCoverTier)
      ) {
        tiers.push(entry as DropoffCoverTier);
      }
    }
    return tiers;
  }

  private resolvePaypalEnabled(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: settings?.IS_PAYPAL_ENABLED,
      variant: null,
      fromVariant: () => undefined,
      fallback: false,
    });
  }

  private resolveBookingWizardAccumulatedStorageRate(
    settings: SharedAppSettings | undefined,
    variant: string | null,
  ): boolean {
    return resolveFeature<boolean>({
      flag: settings?.bookingWizardAccumulatedStorageRate,
      variant,
      // No experiment variant maps to it yet — a plain presentation setting.
      fromVariant: () => undefined,
      fallback: false,
    });
  }

  private resolveServiceFeeFootnote(
    settings: SharedAppSettings | undefined,
    storageLocation: BookableStorageLocation | undefined,
  ): boolean {
    if (!storageLocation) {
      return false;
    }
    if (settings?.isServiceFeeFootnoteEnabled) {
      return true;
    }
    return settings?.serviceFeeFootnoteCities?.includes(storageLocation.officialLocationKey) ?? false;
  }

  private resolvePickupReviewFlow(settings: SharedAppSettings | undefined, variant: string | null): PickupReviewFlow {
    return resolveFeature<PickupReviewFlow>({
      flag: settings?.pickupReviewFlow,
      variant,
      fromVariant: (v) => {
        switch (v) {
          case AppConfig.EXPERIMENT_VARIANTS.pickupReviewInPickupModal:
            return 'in-pickup-modal';
          case AppConfig.EXPERIMENT_VARIANTS.pickupReviewPostValidation:
            return 'post-validation';
          default:
            return undefined;
        }
      },
      fallback: 'none',
    });
  }

  private resolveDailyPricingDefault(_settings: SharedAppSettings | undefined, variant: string | null): boolean {
    return resolveFeature<boolean>({
      // No settings flag yet. To promote daily pricing to a permanent default, add a typed
      // key to SharedAppSettings and read it here — the precedence handles the rest.
      flag: undefined,
      variant,
      fromVariant: (v) => (v === AppConfig.EXPERIMENT_VARIANTS.dailyPricingByDefault ? true : undefined),
      fallback: false,
    });
  }

  private resolveExternalReviewMinRating(settings: SharedAppSettings | undefined, variant: string | null): number {
    return resolveFeature<number>({
      flag: settings?.externalReviewMinRating,
      variant,
      // Plain operational setting — no experiment variant maps to it.
      fromVariant: () => undefined,
      fallback: 4,
    });
  }

  private resolveStoreReviewMinRating(settings: SharedAppSettings | undefined, variant: string | null): number {
    return resolveFeature<number>({
      flag: settings?.storeReviewMinRating,
      variant,
      // Plain operational setting — no experiment variant maps to it.
      fromVariant: () => undefined,
      fallback: 4,
    });
  }

  private resolveTrustpilotReviewUrl(settings: SharedAppSettings | undefined, variant: string | null): string {
    return resolveFeature<string>({
      // A defined value wins outright — including `''`, which deliberately disables the fallback.
      flag: settings?.trustpilotReviewUrl,
      variant,
      // Plain operational setting — no experiment variant maps to it.
      fromVariant: () => undefined,
      // Compose-ready /evaluate/ deep link — lands the traveler straight on the rating step
      // (Trustpilot's standard direct review link), rather than the /review/ profile page.
      fallback: 'https://www.trustpilot.com/evaluate/www.luggagehero.com',
    });
  }

  private resolvePremiumInsuranceEnabled(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: settings?.bookingWizardPremiumInsuranceEnabled,
      variant: null,
      // Plain rollout switch — no experiment variant maps to it.
      fromVariant: () => undefined,
      fallback: false,
    });
  }

  private resolveCoverageDefault(settings: SharedAppSettings | undefined): BookingWizardCoverageDefault {
    const raw = settings?.bookingWizardCoverageDefault;
    return resolveFeature<BookingWizardCoverageDefault>({
      flag: raw === 'basic' || raw === 'none' ? raw : undefined,
      variant: null,
      // A rollout switch for the split test's second run, not an experiment arm: the flag decides for
      // everyone who sees the wizard, and the experiment only decides who sees the wizard.
      fromVariant: () => undefined,
      fallback: 'none',
    });
  }

  private resolveCoverageSkipLink(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: settings?.bookingWizardCoverageSkipLink,
      variant: null,
      // A presentation switch, not an experiment — no variant maps to it. If it becomes an A/B, add
      // the mapping here; the flag still wins, which is how a winning arm is promoted to everyone.
      fromVariant: () => undefined,
      fallback: false,
    });
  }

  private resolveBookingWizardServiceFeeNote(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: settings?.bookingWizardServiceFeeNote,
      variant: null,
      fromVariant: () => undefined,
      fallback: false,
    });
  }

  private resolveConfirmNoCoverage(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: settings?.bookingWizardConfirmNoCoverage,
      variant: null,
      fromVariant: () => undefined,
      // Asks by default — see the getter: this one is not a presentation choice.
      fallback: true,
    });
  }

  private resolveFreeCancellationBanner(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: settings?.bookingWizardFreeCancellationBanner,
      variant: null,
      fromVariant: () => undefined,
      fallback: false,
    });
  }

  private resolvePendingActivationEducationEnabled(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: settings?.pendingActivationEducationEnabled,
      variant: null,
      // Plain kill switch — no experiment variant maps to it.
      fromVariant: () => undefined,
      fallback: true,
    });
  }

  private resolveGuestDropoffFirstStepHint(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      // strictBoolean: a mistyped row ("false" as a string) falls back to on rather than flipping it.
      flag: strictBoolean(settings?.guestDropoffFirstStepHint),
      variant: null,
      // Plain kill switch — no experiment variant maps to it.
      fromVariant: () => undefined,
      fallback: true,
    });
  }

  private resolveOnboardingGuideBannerEnabled(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: settings?.onboardingGuideBannerEnabled,
      variant: null,
      // Plain kill switch — no experiment variant maps to it.
      fromVariant: () => undefined,
      fallback: true,
    });
  }

  private resolveHotelsHeroMarketplaceHybrid(
    settings: SharedAppSettings | undefined,
    storageLocation: BookableStorageLocation | undefined,
  ): boolean {
    if (!storageLocation?._id) {
      return false;
    }
    const services = storageLocation.services ?? [];
    if (!services.includes(StorageLocationService.GuestLuggageStorage)) {
      // A shop without guest storage has no guest flow to fork.
      return false;
    }
    return settings?.hotelsHeroMarketplaceHybrid?.includes(storageLocation._id) ?? false;
  }

  private resolveNewGuestDropoffFlow(
    settings: SharedAppSettings | undefined,
    variant: string | null,
    storageLocation: BookableStorageLocation | undefined,
  ): boolean {
    if (!storageLocation) {
      return false;
    }

    const services = storageLocation.services ?? [];
    if (!services.includes(StorageLocationService.GuestLuggageStorage)) {
      // The shop doesn't offer guest storage at all — the new guest flow never applies.
      return false;
    }

    if (this.resolveHotelsHeroMarketplaceHybrid(settings, storageLocation)) {
      // The hand-off switch wins over the old-flow denylist, so the shop's denylist entry can stay put:
      // switching the hand-off off then lands it back on the old flow, not on a guest-only new flow.
      // The config is not visible here; the guest host falls back to the legacy flow itself when the
      // config does not ask the question (`guestQuestionMissing`).
      return true;
    }

    const forcedToOldFlow = settings?.storageLocationsUsingOldGuestDropoffFlow?.includes(storageLocation._id) ?? false;
    if (!forcedToOldFlow) {
      return true;
    }

    // Forced to the old flow by settings, unless the guest-only experiment opts this shop in.
    const isGuestOnly = services.length === 1 && services[0] === StorageLocationService.GuestLuggageStorage;
    return isGuestOnly && variant === AppConfig.EXPERIMENT_VARIANTS.newGuestDropoffFlow;
  }

  /**
   * Builds a reactive stream for a resolver. `combineLatest` emits only after BOTH sources
   * have produced a value — `appSettings.$current` (after settings load) and
   * `splitTesting.variant$` (a ReplaySubject emitted in `init()`) — so the stream never
   * resolves with a premature/null variant, and `firstValueFrom(...)` waits for init.
   */
  private resolveRecommendedBadgeVariant(settings: SharedAppSettings | undefined): RecommendedBadgeVariant {
    return resolveFeature<RecommendedBadgeVariant>({
      flag: settings?.recommendedBadgeVariant,
      variant: null,
      fromVariant: () => undefined,
      fallback: 'superhero',
    });
  }

  /**
   * Whether the dashboard offers shops the "Check in" action (AM-4874). Default-closed, which also
   * covers the window before settings load: the action stays hidden until an environment turns it on.
   */
  public get isBookingCheckInEnabled(): boolean {
    return this.resolveBookingCheckInEnabled(this.appSettings.current);
  }
  public readonly isBookingCheckInEnabled$: Observable<boolean> = this.stream((s) =>
    this.resolveBookingCheckInEnabled(s),
  );
  private resolveBookingCheckInEnabled(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: strictBoolean(settings?.isBookingCheckInEnabled),
      variant: null,
      // Plain rollout switch — no experiment variant maps to it.
      fromVariant: () => undefined,
      fallback: false,
    });
  }

  /**
   * Whether the dashboard offers shops the "Check out" action (AM-4874). Default-OPEN, deliberately
   * the opposite of the check-in switch: check-out is live and in use, so saying nothing must leave it
   * as it is — only an explicit `false` withdraws it.
   */
  public get isBookingCheckOutEnabled(): boolean {
    return this.resolveBookingCheckOutEnabled(this.appSettings.current);
  }
  public readonly isBookingCheckOutEnabled$: Observable<boolean> = this.stream((s) =>
    this.resolveBookingCheckOutEnabled(s),
  );
  private resolveBookingCheckOutEnabled(settings: SharedAppSettings | undefined): boolean {
    return resolveFeature<boolean>({
      flag: strictBoolean(settings?.isBookingCheckOutEnabled),
      variant: null,
      // Plain rollout switch — no experiment variant maps to it.
      fromVariant: () => undefined,
      fallback: true,
    });
  }

  private stream<T>(project: (settings: SharedAppSettings | undefined, variant: string | null) => T): Observable<T> {
    return combineLatest([this.appSettings.$current, this.splitTesting.variant$]).pipe(
      map(([settings, variant]) => project(settings, variant)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
